import { TFunction } from 'i18next'
import type { ColumnMapping } from '@/components/ExcelExportModal'
import countries from '@/modules/Employee/data/countries.json'

import religions from '@/modules/Employee/data/religions.json'
import nations from '@/modules/Employee/data/nations.json'

export const COUNTRIES = countries
export const RELIGIONS = religions
export const NATIONS = nations

// Giá trị lưu DB bị khoá bởi chk_employee_cv_source — đổi phải kèm migration + validator backend.
export const EMPLOYEE_CV_SOURCE = (t: TFunction<'translation', undefined>) => [
    { label: t('employee:cv_source_social'), value: 'social' },
    { label: t('employee:cv_source_company_form'), value: 'company_form' },
    { label: t('employee:cv_source_paid_job_site'), value: 'paid_job_site' },
    {
        label: t('employee:cv_source_external_referral'),
        value: 'external_referral',
    },
    {
        label: t('employee:cv_source_internal_referral'),
        value: 'internal_referral',
    },
]

export const EMPLOYEE_CONTRACT_TYPE = (
    t: TFunction<'translation', undefined>
) => [
    { label: t('Intern'), value: 'intern' },
    { label: t('Collaborator'), value: 'collaborator' },
    { label: t('Probation'), value: 'probation' },
    { label: t('Official'), value: 'official' },
    { label: t('Advisor'), value: 'advisor' },
]

// Seniority labels kept in English per HR spec
export const EMPLOYEE_SENIORITY_LEVEL: { label: string; value: string }[] = [
    { label: 'Intern', value: 'intern' },
    { label: 'Executive', value: 'executive' },
    { label: 'Senior', value: 'senior' },
    { label: 'Lead', value: 'lead' },
    { label: 'Specialist', value: 'specialist' },
    { label: 'Manager', value: 'manager' },
    { label: 'Director', value: 'director' },
    { label: 'Expert', value: 'expert' },
]

// External-facing title (chức danh đối ứng) — enumerated per HR spec, stored as free text
export const EMPLOYEE_EXTERNAL_TITLE: { label: string; value: string }[] = [
    { label: 'Business Development', value: 'Business Development' },
    { label: 'Solution Specialist', value: 'Solution Specialist' },
]

export const EMPLOYEE_STATUS = (t: TFunction<'translation', undefined>) => [
    { label: t('employee_status_active'), value: 1 },
    { label: t('employee_status_inactive'), value: 0 },
]

// Status map for quick lookup (used in tables without t function)
export const EMPLOYEE_STATUS_MAP: Record<
    number,
    { label: string; className: string }
> = {
    1: {
        label: 'employee_status_active',
        className: 'bg-green-100 text-green-700',
    },
    0: {
        label: 'employee_status_inactive',
        className: 'bg-gray-100 text-gray-700',
    },
}

export const EMPLOYEE_GENDER = (t: TFunction<'translation', undefined>) => [
    {
        label: t('Male'),
        value: 'male',
    },
    {
        label: t('Female'),
        value: 'female',
    },
    {
        label: t('Other'),
        value: 'other',
    },
]

export const EMPLOYEE_MARRIAGE_STATUS = (
    t: TFunction<'translation', undefined>
) => [
    {
        label: t('single'),
        value: 'single',
    },
    {
        label: t('married'),
        value: 'married',
    },
    {
        label: t('undetermined'),
        value: 'undetermined',
    },
]

export const ATTENDANCE_TYPES = (t: TFunction<'translation', undefined>) => [
    {
        label: t('Check In'),
        value: 'check_in',
    },
    {
        label: t('Check Out'),
        value: 'check_out',
    },
    {
        label: t('Break Start'),
        value: 'break_start',
    },
    {
        label: t('Break End'),
        value: 'break_end',
    },
    {
        label: t('Overtime Start'),
        value: 'overtime_start',
    },
    {
        label: t('Overtime End'),
        value: 'overtime_end',
    },
]

export const ATTENDANCE_STATUS = (t: TFunction<'translation', undefined>) => [
    {
        label: t('On Time'),
        value: 'on_time',
    },
    {
        label: t('Late'),
        value: 'late',
    },
    {
        label: t('Early'),
        value: 'early',
    },
    {
        label: t('Absent'),
        value: 'absent',
    },
    {
        label: t('Excused'),
        value: 'excused',
    },
]

export const DOCUMENT_TYPES = (t: TFunction<'translation', undefined>) => [
    {
        label: t('employees.front_of_cccd'),
        value: 'front_of_cccd',
    },
    {
        label: t('employees.back_of_cccd'),
        value: 'back_of_cccd',
    },
    {
        label: t('employees.avatar'),
        value: 'avatar',
    },
    {
        label: t('employees.signature', { ns: 'employee' }),
        value: 'signature',
    },
    {
        label: t('employees.cv', { ns: 'employee' }),
        value: 'cv',
    },
    {
        label: t('employees.bio', { ns: 'employee' }),
        value: 'bio',
    },
    {
        label: t('employees.health', { ns: 'employee' }),
        value: 'health',
    },
    {
        label: t('document.certificate', { ns: 'employee' }),
        value: 'certificate',
    },
]

export const CAREER_PATH_STAGES = (t: TFunction<'translation', undefined>) => [
    {
        value: 'intern' as const,
        label: t('career_path.intern'),
        color: 'bg-blue-100 text-blue-700',
    },
    {
        value: 'probation' as const,
        label: t('career_path.probation'),
        color: 'bg-yellow-100 text-yellow-700',
    },
    {
        value: 'official' as const,
        label: t('career_path.official'),
        color: 'bg-green-100 text-green-700',
    },
    {
        value: 'offboard' as const,
        label: t('career_path.offboard'),
        color: 'bg-gray-100 text-gray-700',
    },
]

// Centralized at @/constants/departments — re-export for backward compat
export { DEPARTMENT_SCOPES } from '@/constants/departments'

export const DEPARTMENT_STATUS = (t: TFunction<'translation', undefined>) => [
    { value: 'active', label: t('status_active') },
    { value: 'inactive', label: t('status_inactive') },
]

export const DEPARTMENT_EXCEL_COLUMNS: ColumnMapping[] = [
    {
        header: 'department_code',
        data_field: 'department_code',
    },
    {
        header: 'department_name',
        data_field: 'department_name',
    },
    {
        header: 'department_leader',
        data_field: 'department_leader_name',
    },
    {
        header: 'description',
        data_field: 'department_description',
    },
    {
        header: 'status',
        data_field: 'department_status',
        formatter_type: 'department_status',
    },
    {
        header: 'created_at',
        data_field: 'department_created_at',
        formatter_type: 'date',
        formatter_options: {
            date_format: 'DD/MM/YYYY',
        },
    },
]
